import { useSettlePolling } from '../hooks/useSettlePolling'
import { ClockIcon } from './icons'

export interface CheckoutTabWaitProps {
  /** The Stripe Checkout page, for a customer whose tab did not open or who closed it. */
  checkoutUrl: string
  /**
   * Asks whether the payment has settled. Resolves true once it has (the
   * widget has moved on) and false to keep waiting. Should not throw; a
   * rejection is treated as "keep waiting".
   */
  poll: () => Promise<boolean>
  /** Gives up on this payment and frees the time. */
  onChooseAnotherTime: () => void
  busy?: boolean
  error?: string | null
  /** How often to ask while the widget is in view. */
  intervalMs?: number
}

export const CHECKOUT_TAB_POLL_MS = 4000

/**
 * Shown while the customer pays in a new tab because Checkout could not take
 * over the page (ACA-99). Asks the server every few seconds while the widget
 * is in view, and at once whenever the customer comes back to this tab.
 */
export function CheckoutTabWait({
  checkoutUrl,
  poll,
  onChooseAnotherTime,
  busy = false,
  error = null,
  intervalMs = CHECKOUT_TAB_POLL_MS,
}: CheckoutTabWaitProps) {
  useSettlePolling(poll, intervalMs)

  return (
    <div className="ac-confirmation" role="status">
      <div className="ac-confirmation__badge" aria-hidden="true">
        <ClockIcon size={30} />
      </div>

      <div className="ac-stack ac-stack--tight">
        <h3 className="ac-heading ac-heading--display">Finish paying in the new tab</h3>
        <p className="ac-prose">
          We'll confirm your booking here. Your time is held while you pay.
        </p>
      </div>

      {error ? (
        <div className="ac-notice ac-notice--error" role="alert">
          <span>{error}</span>
        </div>
      ) : null}

      <a className="ac-btn ac-btn--primary" href={checkoutUrl} target="_blank" rel="noopener noreferrer">
        Open the payment page
      </a>
      <button
        type="button"
        className="ac-btn ac-btn--ghost"
        onClick={onChooseAnotherTime}
        disabled={busy}
      >
        Choose another time
      </button>
    </div>
  )
}
